import { formatHours } from "../../utils/date";

// All DTR queries share this prefix so one invalidation refreshes them.
export const DTR_QUERY_KEY = "me:dtr";

// A cut-off for one employee: there is no submit. Exporting it for payroll
// approves it (HR may approve early) and locks it (Processed).
export const CUTOFF_STATUS = {
  OPEN: { label: "Open", tone: "gray" },
  APPROVED: { label: "Approved", tone: "green" },
  EXPORTED: { label: "Processed", tone: "blue" }, // sent to payroll, locked
};
export const getCutoffStatus = (status) =>
  CUTOFF_STATUS[status ?? "OPEN"] ?? { label: status, tone: "gray" };

export const DAY_STATUS = {
  PRESENT: { label: "Present", tone: "green" },
  LATE: { label: "Late", tone: "amber" },
  ABSENT: { label: "Absent", tone: "red" },
  HALF_DAY: { label: "Half Day", tone: "purple" },
  ON_LEAVE: { label: "On Leave", tone: "blue" },
  HOLIDAY: { label: "Holiday", tone: "purple" },
  REST_DAY: { label: "Rest day", tone: "gray" },
  NO_RECORD: { label: "No record", tone: "gray" },
};

export const formatHoursOrDash = (hours) => {
  const value = Number(hours);
  return hours != null && value > 0 ? formatHours(value) : "—";
};

export const formatTotalHours = (hours) => formatHours(Number(hours) || 0);

/** The cut-off totals as label / value pairs (with an icon), in display order. */
export const getTotalItems = (totals) => [
  { label: "Days", value: totals.totalDays, icon: "bi-calendar3" },
  { label: "Present", value: totals.present, icon: "bi-check-circle" },
  { label: "Late", value: totals.late, icon: "bi-clock" },
  { label: "Absent", value: totals.absent, icon: "bi-x-circle" },
  { label: "Half Day", value: totals.halfDay, icon: "bi-circle-half" },
  { label: "On Leave", value: totals.onLeave, icon: "bi-airplane" },
  { label: "No record", value: totals.noRecord, icon: "bi-question-circle" },
  {
    label: "Work Hours",
    value: formatTotalHours(totals.totalWorkHours),
    icon: "bi-hourglass-split",
  },
  {
    label: "Overtime",
    value: formatTotalHours(totals.totalOvertimeHours),
    icon: "bi-plus-circle",
  },
  // Workday 8:00–17:00 (no grace): what payroll deducts
  ...(totals.lateMinutes != null
    ? [
        {
          label: "Late time",
          value: formatMinutes(totals.lateMinutes),
          icon: "bi-alarm",
        },
      ]
    : []),
  ...(totals.undertimeMinutes != null
    ? [
        {
          label: "Undertime",
          value: formatMinutes(totals.undertimeMinutes),
          icon: "bi-box-arrow-left",
        },
      ]
    : []),
];

/** 95 → "1h 35m", 20 → "20m", 0 → "0m". */
export const formatMinutes = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  return h ? `${h}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`;
};

// DOM id of a day's row (to scroll to it).
export const getDayRowId = (date) => `dtr-day-${date}`;

// My DTR deep link to a cut-off ({ year, month, cutoff }).
export const dtrLink = ({ year, month, cutoff }) =>
  `/me/dtr?year=${year}&month=${month}&cutoff=${cutoff}`;

/** "year-month-cutoff" key of a period ({ year, month, cutoff }). */
export const periodKeyOf = (p) => (p ? `${p.year}-${p.month}-${p.cutoff}` : "");

/**
 * Can the employee ask for a correction on this day? Past or today, not
 * locked (exported), no correction already pending, not a future day.
 */
export const canRequestFix = (day, { locked, todayKey }) =>
  !locked && !day.future && day.date <= todayKey && !day.pendingCorrection;
